<template>
  <div class="cat-root">

    <!-- date range: the same calendar bar the dashboards use -->
    <calendar-bar v-model="dateRange" sync-with-value @input="refresh"/>

    <!-- toolbar: type chips -->
    <div class="cat-toolbar">
      <div class="cat-types">
        <button class="cat-chip" :class="{ 'cat-chip--on': selectedTypes.length === 0 }" @click="selectAllTypes">
          {{ $t('clientActivity.all') }}
        </button>
        <button v-for="type in allTypes" :key="type"
                class="cat-chip" :class="[`cat-chip--${type.toLowerCase()}`, { 'cat-chip--on': selectedTypes.includes(type) }]"
                @click="toggleType(type)">
          <i :class="iconOf(type)" class="mr-25"></i>{{ $t(`clientActivity.types.${type}`) }}
        </button>
      </div>
      <b-button variant="outline-primary" size="sm" class="cat-refresh" :disabled="loading" @click="refresh">
        <i class="fa-solid fa-rotate mr-25"></i>{{ $t('operations.refresh') }}
      </b-button>
    </div>

    <!-- agent filter: everyone who did something to this client, most active first.
         Click = highlight their events + jump to the latest one. -->
    <div v-if="actors.length" class="cat-actors">
      <span class="cat-actors__label"><i class="fa-solid fa-user-tie mr-25"></i>{{ $t('clientActivity.byAgent') }}</span>
      <button v-for="actor in actors" :key="actor.key"
              class="cat-chip" :class="{ 'cat-chip--on': selectedActorKey === actor.key }"
              @click="toggleActor(actor.key)">
        {{ actor.name }}<span class="cat-chip__count">{{ actor.count }}</span>
      </button>
    </div>

    <!-- In the client modal the timeline scrolls inside its own box; the
         standalone page scrolls with the document. -->
    <div :class="{ 'cat-scroll': scrollable }">
    <b-overlay :show="loading" rounded="sm" spinner-variant="primary">
      <app-timeline v-if="events.length" class="cat-timeline">
        <app-timeline-item v-for="(event, index) in events" :key="index" :variant="variantOf(event)">
          <div class="cat-item"
               :class="{
                 'cat-item--flash': flashIndex === index,
                 'cat-item--actor': selectedActorKey && actorKeyOf(event) === selectedActorKey,
                 'cat-item--dim': selectedActorKey && actorKeyOf(event) !== selectedActorKey,
               }"
               :ref="`evt-${index}`">
            <div v-if="dayLabelOf(index)" class="cat-day">
              <span class="cat-day__chip"><i class="fa-regular fa-calendar mr-25"></i>{{ dayLabelOf(index) }}</span>
            </div>
            <div class="cat-item__head">
              <span class="cat-item__title">
                <i :class="iconOf(event.type)" class="mr-25"></i>{{ titleOf(event) }}
              </span>
              <span class="cat-item__time" v-b-tooltip.hover :title="fullTime(event.time)">
                {{ shortTime(event.time) }}<span class="cat-item__ago"> · {{ agoTime(event.time) }}</span>
              </span>
            </div>

            <div v-if="actorOf(event)" class="cat-item__who">
              <i class="fa-solid fa-user mr-25"></i>{{ actorOf(event) }}
            </div>

            <!-- FIELD_CHANGE: old → new per field -->
            <div v-if="event.type === 'FIELD_CHANGE' && event.changes && event.changes.length" class="cat-changes">
              <div v-for="change in event.changes" :key="change.field" class="cat-change">
                <span class="cat-change__field">{{ change.field }}</span>
                <span class="cat-change__old">{{ fmtField(change.field, change.oldValue) }}</span>
                <i class="fa-solid fa-arrow-right cat-change__arrow"></i>
                <span class="cat-change__new">{{ fmtField(change.field, change.newValue) }}</span>
              </div>
            </div>
            <div v-else-if="event.type === 'FIELD_CHANGE' && event.description" class="cat-item__desc">
              {{ event.description }}
            </div>

            <!-- ASSIGNMENT -->
            <div v-else-if="event.type === 'ASSIGNMENT'" class="cat-item__desc">
              <span>{{ $t('clientActivity.assignedTo') }} <b>{{ userName(event.data.userId) }}</b></span>
              <span v-if="event.data.previousUserId" class="text-muted"> ({{ $t('clientActivity.previously') }} {{ userName(event.data.previousUserId) }})</span>
              <span v-if="event.data.businessUnitId" class="text-muted"> · {{ getBusinessUnitNameById(event.data.businessUnitId) }}</span>
              <b-badge v-if="event.data.source" variant="light-secondary" class="ml-25">{{ event.data.source }}</b-badge>
            </div>

            <!-- TRADES -->
            <div v-else-if="event.type === 'TRADE_OPENED' || event.type === 'TRADE_CLOSED'" class="cat-item__desc">
              <b-badge variant="light-primary" class="mr-25">#{{ event.data.order }}</b-badge>
              <b>{{ event.data.symbolName }}</b>
              <b-badge :variant="event.data.command === 'BUY' ? 'light-success' : 'light-danger'" class="ml-25">{{ event.data.command }}</b-badge>
              <span v-if="event.data.price != null" class="text-muted"> @ {{ event.data.price }}</span>
              <b-badge v-if="event.type === 'TRADE_CLOSED' && event.data.profit != null"
                       :variant="event.data.profit >= 0 ? 'light-success' : 'light-danger'" class="ml-50">
                {{ event.data.profit >= 0 ? '+' : '' }}{{ Number(event.data.profit).toFixed(2) }}
              </b-badge>
              <!-- No CRM tracker row → the client made it on the platform (or it predates tracking).
                   Agent-made trades show the agent's name on the "who" line above instead. -->
              <b-badge v-if="!event.userId && !event.userName" variant="light-info" class="ml-50">
                <i class="fa-solid fa-user mr-25"></i>{{ $t('clientActivity.byClient') }}
              </b-badge>
            </div>

            <!-- CALL -->
            <div v-else-if="event.type === 'CALL'" class="cat-item__desc">
              <b-badge :variant="event.data.success ? 'light-success' : 'light-danger'" class="mr-25">
                {{ event.data.success ? $t('clientActivity.success') : $t('clientActivity.failed') }}
              </b-badge>
              <span v-if="event.data.destination" class="text-muted">{{ event.data.destination }}</span>
              <span v-if="event.data.reason" class="text-muted"> · {{ event.data.reason }}</span>
            </div>

            <!-- LOGIN -->
            <div v-else-if="event.type === 'LOGIN'" class="cat-item__desc">
              <b-badge :variant="event.data.failed ? 'light-danger' : 'light-success'" class="mr-25">
                {{ event.data.failed ? $t('clientActivity.failed') : $t('clientActivity.success') }}
              </b-badge>
              <span v-if="event.data.ip" class="text-muted">{{ event.data.ip }}</span>
              <span v-if="event.data.host" class="text-muted"> · {{ event.data.host }}</span>
              <span v-if="event.data.reason" class="text-muted"> · {{ event.data.reason }}</span>
            </div>

            <!-- MONETARY -->
            <div v-else-if="event.type === 'MONETARY'" class="cat-item__desc">
              <b-badge v-if="event.data.deleted" variant="danger" class="mr-25">
                <i class="fa-solid fa-trash mr-25"></i>{{ $t('clientActivity.deleted') }}
              </b-badge>
              <b-badge v-else-if="event.data.deletedLater" variant="light-warning" class="mr-25">
                <i class="fa-solid fa-trash mr-25"></i>{{ $t('clientActivity.deletedLater') }}
              </b-badge>
              <b-badge v-else-if="event.data.changed" variant="light-info" class="mr-25">
                <i class="fa-solid fa-pen mr-25"></i>{{ $t('clientActivity.changed') }}
              </b-badge>
              <span class="cat-amount" :class="[
                      event.data.monetaryType === 'WITHDRAWAL' || (event.data.amount || 0) < 0 ? 'cat-amount--neg' : 'cat-amount--pos',
                      { 'cat-amount--deleted': event.data.deleted },
                    ]">
                {{ Number(event.data.amount || 0).toLocaleString() }} {{ event.data.currency }}
              </span>
              <b-badge v-if="event.data.status" variant="light-secondary" class="ml-25">{{ event.data.status }}</b-badge>
              <b-badge v-if="event.data.action === 'CREDIT' || event.data.action === 'BONUS'" variant="light-info" class="ml-25">BONUS</b-badge>
              <b-badge v-else-if="event.data.action === 'TAXES'" variant="light-secondary" class="ml-25">TAXES</b-badge>
              <b-badge v-if="event.data.realType === 'REAL'" variant="light-success" class="ml-25">REAL</b-badge>
              <b-badge v-if="event.data.isFtd" variant="light-warning" class="ml-25">FTD</b-badge>
              <b-badge v-if="event.data.isStd" variant="light-info" class="ml-25">STD</b-badge>
              <b-badge v-if="event.data.ftdType" variant="light-warning" class="ml-25">{{ String(event.data.ftdType).replace('_', ' ') }}</b-badge>
              <b-badge v-if="event.data.realType === 'FAKE'" variant="light-danger" class="ml-25">FAKE</b-badge>
              <span v-if="event.data.changed" class="cat-change ml-50" style="display: inline-flex;">
                <span class="cat-change__field">{{ event.data.field }}</span>
                <span class="cat-change__old">{{ fmt(event.data.oldValue) }}</span>
                <i class="fa-solid fa-arrow-right cat-change__arrow"></i>
                <span class="cat-change__new">{{ fmt(event.data.newValue) }}</span>
              </span>
            </div>

            <!-- NOTE -->
            <div v-else-if="event.type === 'NOTE'" class="cat-item__note">
              <i class="fa-solid fa-quote-left mr-25 text-muted"></i>{{ event.data.text }}
            </div>

            <div v-else-if="event.description" class="cat-item__desc text-muted">{{ event.description }}</div>
          </div>
        </app-timeline-item>
      </app-timeline>

      <div v-else-if="!loading" class="cat-empty">
        <i class="fa-solid fa-timeline cat-empty__icon"></i>
        <div>{{ $t('clientActivity.empty') }}</div>
      </div>
    </b-overlay>

    <div v-if="hasMore" class="text-center mt-1">
      <b-button variant="outline-primary" size="sm" :disabled="loading" @click="loadMore">
        {{ $t('clientActivity.loadMore') }}
      </b-button>
    </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import AppTimeline from '@core/components/app-timeline/AppTimeline.vue';
import AppTimelineItem from '@core/components/app-timeline/AppTimelineItem.vue';
import CalendarBar from '@/components/widget/CalendarBar.vue';
import mixinNotifications from '@/components/mixin/mixinNotifications';

const ALL_TYPES = ['CREATED', 'FIELD_CHANGE', 'ASSIGNMENT', 'TRADE_OPENED', 'TRADE_CLOSED', 'CALL', 'LOGIN', 'MONETARY', 'NOTE'];
const PAGE_SIZE = 100;

export default {
  name: 'ClientActivityTimeline',
  components: { AppTimeline, AppTimelineItem, CalendarBar },
  mixins: [mixinNotifications],
  props: {
    clientId: {
      type: String,
      default: null
    },
    // In the client modal the timeline scrolls inside its own box.
    scrollable: {
      type: Boolean,
      default: false
    },
  },
  data: () => ({
    allTypes: ALL_TYPES,
    selectedTypes: [],
    // {from, until} in YYYY-MM-DD — driven by the calendar bar; nulls = all time.
    dateRange: { from: null, until: null },
    events: [],
    page: 1,
    pages: 0,
    loading: false,
    flashIndex: null,
    selectedActorKey: null,
  }),
  computed: {
    ...mapGetters('data', ['getUserFullName', 'getBusinessUnitNameById']),
    ...mapGetters('clients', ['allStatuses']),
    statusById() {
      return (this.allStatuses || []).reduce((map, status) => {
        map[status.id] = status.status;
        return map;
      }, {});
    },
    hasMore() {
      return this.page < this.pages;
    },
    // Everyone who did something in the loaded events, most active first.
    actors() {
      const map = {};
      this.events.forEach(event => {
        const key = this.actorKeyOf(event);
        if (!key) return;
        if (!map[key]) {
          map[key] = { key, name: this.actorOf(event) || key, count: 0 };
        }
        map[key].count += 1;
      });
      return Object.values(map).sort((a, b) => b.count - a.count);
    },
  },
  watch: {
    clientId: {
      handler() {
        this.refresh();
      },
      immediate: true
    },
    selectedTypes() {
      this.refresh();
    },
  },
  methods: {
    ...mapActions('clients', ['getClientActivity']),
    async refresh() {
      if (!this.clientId) {
        this.events = [];
        this.selectedActorKey = null;
        return;
      }
      this.page = 1;
      this.selectedActorKey = null;
      await this.fetch(false);
    },
    actorKeyOf(event) {
      return event.userId || event.userName || null;
    },
    toggleActor(key) {
      if (this.selectedActorKey === key) {
        this.selectedActorKey = null;
        return;
      }
      this.selectedActorKey = key;
      const index = this.events.findIndex(event => this.actorKeyOf(event) === key);
      if (index >= 0) {
        this.$nextTick(() => this.jumpToEvent(index));
      }
    },
    // Server times are UTC — always convert to the viewer's local zone
    // (same rule as the global momentDateTime filter).
    localMoment(time) {
      return this.$moment.utc(time).local();
    },
    dayLabelOf(index) {
      const event = this.events[index];
      if (!event) return null;
      const day = this.localMoment(event.time);
      if (index > 0 && day.isSame(this.localMoment(this.events[index - 1].time), 'day')) {
        return null;
      }
      if (day.isSame(this.$moment(), 'day')) return this.$t('clientActivity.today');
      if (day.isSame(this.$moment().subtract(1, 'day'), 'day')) return this.$t('clientActivity.yesterday');
      return day.format('dddd, DD MMM YYYY');
    },
    shortTime(time) {
      return this.localMoment(time).format('HH:mm');
    },
    fullTime(time) {
      return this.localMoment(time).format('dddd, DD MMMM YYYY HH:mm:ss');
    },
    agoTime(time) {
      return this.localMoment(time).fromNow();
    },
    async loadMore() {
      this.page += 1;
      await this.fetch(true);
    },
    async fetch(append) {
      this.loading = true;
      try {
        const result = await this.getClientActivity({
          clientId: this.clientId,
          types: this.selectedTypes,
          range: { from: this.dateRange.from || null, until: this.dateRange.until || null },
          pagination: { page: this.page, size: PAGE_SIZE },
        });
        this.events = append ? this.events.concat(result.data) : result.data;
        this.pages = result.pagination.pages;
      } catch (err) {
        this.showErrorNotification(this.$t('clientActivity.error'));
      } finally {
        this.loading = false;
      }
    },
    toggleType(type) {
      if (this.selectedTypes.includes(type)) {
        this.selectedTypes = this.selectedTypes.filter(t => t !== type);
      } else {
        this.selectedTypes = [...this.selectedTypes, type];
      }
    },
    selectAllTypes() {
      this.selectedTypes = [];
    },
    jumpToEvent(index) {
      const ref = this.$refs[`evt-${index}`];
      const el = Array.isArray(ref) ? ref[0] : ref;
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        this.flashIndex = index;
        clearTimeout(this._flashTimer);
        this._flashTimer = setTimeout(() => { this.flashIndex = null; }, 1200);
      }
    },
    titleOf(event) {
      const key = `clientActivity.titles.${event.type}`;
      const translated = this.$t(key);
      return translated === key ? (event.title || event.type) : translated;
    },
    actorOf(event) {
      if (event.userName) return event.userName;
      if (event.userId) return this.userName(event.userId);
      return null;
    },
    userName(userId) {
      if (!userId) return '—';
      return this.getUserFullName(userId) || userId;
    },
    statusName(statusId) {
      return this.statusById[statusId] || statusId;
    },
    // Field-aware formatting: ids of statuses / business units / users render
    // as their names, everything else falls through to fmt().
    fmtField(field, value) {
      if (value != null && value !== '' && value !== '<null>' && value !== 'null') {
        if (field === 'statusId') return this.statusName(String(value));
        if (field === 'businessUnitId') return this.getBusinessUnitNameById(String(value)) || value;
        if (field === 'representativeUserId' || field === 'ftdOwnerId' || field === 'stdOwnerId'
            || field === 'presaleUserId' || field === 'retentionUserId') {
          return this.userName(String(value));
        }
      }
      return this.fmt(value);
    },
    fmt(value) {
      // Tracker diffs render java nulls as the literal string "<null>".
      if (value === null || value === undefined || value === '' || value === '<null>' || value === 'null') {
        return this.$t('clientActivity.emptyValue');
      }
      if (value === true || value === 'true') return this.$t('clientActivity.yes');
      if (value === false || value === 'false') return this.$t('clientActivity.no');
      const text = typeof value === 'object' ? JSON.stringify(value) : String(value);
      return text.length > 80 ? `${text.slice(0, 80)}…` : text;
    },
    variantOf(event) {
      switch (event.type) {
        case 'CREATED': return 'success';
        case 'FIELD_CHANGE': return 'warning';
        case 'ASSIGNMENT': return 'info';
        case 'TRADE_OPENED': return 'primary';
        case 'TRADE_CLOSED': return event.data && event.data.profit >= 0 ? 'success' : 'danger';
        case 'CALL': return event.data && event.data.success ? 'success' : 'danger';
        case 'LOGIN': return event.data && event.data.failed ? 'danger' : 'info';
        case 'MONETARY': return event.data && (event.data.deleted || event.data.monetaryType === 'WITHDRAWAL') ? 'danger' : 'success';
        case 'NOTE': return 'secondary';
        default: return 'secondary';
      }
    },
    iconOf(type) {
      switch (type) {
        case 'CREATED': return 'fa-solid fa-user-plus';
        case 'FIELD_CHANGE': return 'fa-solid fa-pen-to-square';
        case 'ASSIGNMENT': return 'fa-solid fa-people-arrows';
        case 'TRADE_OPENED': return 'fa-solid fa-arrow-trend-up';
        case 'TRADE_CLOSED': return 'fa-solid fa-flag-checkered';
        case 'CALL': return 'fa-solid fa-phone';
        case 'LOGIN': return 'fa-solid fa-right-to-bracket';
        case 'MONETARY': return 'fa-solid fa-coins';
        case 'NOTE': return 'fa-solid fa-sticky-note';
        default: return 'fa-solid fa-circle-info';
      }
    },
  },
};
</script>

<style scoped lang="scss">
.cat-toolbar {
  display: flex; align-items: flex-start; justify-content: space-between;
  gap: 0.75rem; flex-wrap: wrap; margin-bottom: 0.9rem;
}

.cat-types { display: flex; flex-wrap: wrap; gap: 0.35rem; }

.cat-chip {
  border: 1px solid rgba(110, 117, 159, 0.25); background: transparent;
  border-radius: 999px; padding: 0.28rem 0.7rem;
  font-size: 0.72rem; font-weight: 700; color: #6e6b7b;
  cursor: pointer; transition: all 0.15s ease; white-space: nowrap;

  &:hover { border-color: #7367f0; color: #7367f0; }
  &--on { background: rgba(115, 103, 240, 0.12); border-color: #7367f0; color: #7367f0; }
}

.cat-dates {
  display: flex; align-items: center; gap: 0.4rem; min-width: 0;
  ::v-deep .b-form-datepicker { min-width: 150px; }
}

.cat-actors {
  display: flex; align-items: center; flex-wrap: wrap; gap: 0.35rem;
  margin-bottom: 0.9rem;

  &__label {
    font-size: 0.72rem; font-weight: 700; color: #9a9dad;
    text-transform: uppercase; letter-spacing: 0.04em; margin-right: 0.2rem;
  }
}

.cat-chip__count {
  display: inline-block; margin-left: 0.35rem; padding: 0 0.4rem;
  background: rgba(110, 117, 159, 0.16); border-radius: 999px;
  font-size: 0.68rem; font-variant-numeric: tabular-nums;
}

.cat-day {
  margin: 0.15rem 0 0.4rem;

  &__chip {
    display: inline-block; font-size: 0.72rem; font-weight: 700;
    color: #7367f0; background: rgba(115, 103, 240, 0.1);
    border-radius: 999px; padding: 0.15rem 0.65rem;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
}

.cat-timeline { padding-left: 0.4rem; }

/* Modal mode: the timeline scrolls inside its own box */
.cat-scroll {
  max-height: 58vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-right: 0.5rem;
}

.cat-item {
  border-radius: 10px; padding: 0.35rem 0.5rem; margin-left: -0.5rem;
  transition: background 0.4s ease, opacity 0.25s ease;

  &--flash { background: rgba(115, 103, 240, 0.16); }
  &--actor { background: rgba(115, 103, 240, 0.08); box-shadow: inset 3px 0 0 #7367f0; }
  &--dim { opacity: 0.35; filter: grayscale(0.4); }

  &__head { display: flex; align-items: baseline; justify-content: space-between; gap: 0.75rem; flex-wrap: wrap; }
  &__title { font-weight: 700; color: #5e5873; }
  &__time { font-size: 0.78rem; font-weight: 700; color: #6e6b7b; white-space: nowrap; font-variant-numeric: tabular-nums; }
  &__ago { font-weight: 400; color: #9a9dad; }
  &__who { font-size: 0.75rem; color: #7367f0; font-weight: 600; margin-top: 2px; }
  &__desc { font-size: 0.82rem; margin-top: 3px; }
  &__note { font-size: 0.82rem; font-style: italic; color: #6e6b7b; margin-top: 3px; }
}

.cat-changes { margin-top: 4px; display: flex; flex-direction: column; gap: 3px; }

.cat-change {
  display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap;
  font-size: 0.78rem;

  &__field {
    font-weight: 700; color: #5e5873;
    background: rgba(110, 117, 159, 0.1); border-radius: 6px; padding: 0.1rem 0.45rem;
  }
  &__old { color: #ea5455; text-decoration: line-through; }
  &__arrow { color: #9a9dad; font-size: 0.65rem; }
  &__new { color: #28c76f; font-weight: 700; }
}

.cat-amount {
  font-weight: 800;
  &--pos { color: #28c76f; }
  &--neg { color: #ea5455; }
  &--deleted { text-decoration: line-through; opacity: 0.75; }
}

.cat-empty {
  text-align: center; color: #9a9dad; padding: 2.5rem 1rem;
  &__icon { font-size: 2rem; opacity: 0.5; display: block; margin-bottom: 0.6rem; }
}

body.dark-layout {
  .cat-item__title { color: #d0d2d6; }
  .cat-change__field { color: #d0d2d6; background: rgba(255, 255, 255, 0.08); }
}
</style>
